import { useCallback, useMemo, useState } from 'react'

import { Badge, Checkbox, Modal, Radio, Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheckDouble, faEraser, faFileCsv, faFileExcel, faFileExport, faGraduationCap, faHospitalAlt, faIdCard,
  faMapMarkerAlt, faTimes, faUserTie
} from '@fortawesome/pro-solid-svg-icons'
import { isArray } from 'lodash'
import { useSelector } from 'react-redux'

import IconButton from '../../../../Components/antd/Buttons/IconButton'
import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import { useLocalStorage } from '../../../../hooks/UseLocalStorage'

import '../../../../assets/sectors-export-modal.scss'

/** Column keys understood by the sectors export endpoint. The backend keeps its own column order, whatever we send. */
export const SectorExportColumns = Object.freeze({
  POLE_ID: 'poleId',
  POLE_NAME: 'poleName',
  ID: 'id',
  NAME: 'name',
  COMMENT: 'comment',
  EMPLACEMENT: 'emplacement',
  PHONE_NUMBER: 'phoneNumber',
  RESPONSIBLE_SERVICE_NAME: 'responsibleServiceName',
  RESPONSIBLE_SERVICE_EMAIL: 'responsibleServiceEmail',
  PATHWAY_DETAILS: 'pathwayDetails',
  SECTION_ABBREVIATIONS: 'sectionAbbreviations',
  INSTITUTION_NAME: 'institutionName',
  INSTITUTION_ID: 'institutionId'
})

/** Purely a reading aid for the modal: the export order lives in the backend. */
const SECTIONS = [
  { key: 'identification', icon: faIdCard, columns: [SectorExportColumns.ID, SectorExportColumns.NAME, SectorExportColumns.POLE_ID, SectorExportColumns.POLE_NAME] },
  { key: 'service', icon: faMapMarkerAlt, columns: [SectorExportColumns.EMPLACEMENT, SectorExportColumns.PHONE_NUMBER, SectorExportColumns.COMMENT] },
  { key: 'contact', icon: faUserTie, columns: [SectorExportColumns.RESPONSIBLE_SERVICE_NAME, SectorExportColumns.RESPONSIBLE_SERVICE_EMAIL] },
  { key: 'training', icon: faGraduationCap, columns: [SectorExportColumns.PATHWAY_DETAILS, SectorExportColumns.SECTION_ABBREVIATIONS] },
  { key: 'institution', icon: faHospitalAlt, columns: [SectorExportColumns.INSTITUTION_NAME, SectorExportColumns.INSTITUTION_ID] }
]

const FORMATS = [
  { value: 'xlsx', label: '.xlsx', icon: faFileExcel },
  { value: 'csv', label: '.csv', icon: faFileCsv }
]

const ALL_COLUMNS = SECTIONS.flatMap(s => s.columns)
const STORAGE_KEY = 'sectors-export-columns'
const DEFAULT_FORMAT = 'xlsx'

const SectorsExport = ({ onExport }) => {
  const t = useSelector(selectTranslate)

  const [visible, setVisible] = useState(false)
  const [format, setFormat] = useState(DEFAULT_FORMAT)
  // Only keys still known by this version survive, so a renamed column cannot silently drop out of the export.
  const [columns, setColumns] = useLocalStorage(STORAGE_KEY, ALL_COLUMNS, stored => isArray(stored) ? stored.filter(c => ALL_COLUMNS.includes(c)) : ALL_COLUMNS)

  const selected = useMemo(() => new Set(columns), [columns])

  const okText = useMemo(() => (<> <FontAwesomeIcon icon={faFileExport} /> &nbsp; {t('Export')} </>), [t])
  const cancelText = useMemo(() => (<> <FontAwesomeIcon icon={faTimes} /> &nbsp; {t('Cancel')} </>), [t])

  const handleToggleColumn = useCallback(column => setColumns(prev => (
    prev.includes(column) ? prev.filter(c => c !== column) : ALL_COLUMNS.filter(c => c === column || prev.includes(c))
  )), [setColumns])

  const handleToggleSection = useCallback(section => setColumns(prev => {
    const isFull = section.columns.every(c => prev.includes(c))

    return isFull
      ? prev.filter(c => !section.columns.includes(c))
      : ALL_COLUMNS.filter(c => prev.includes(c) || section.columns.includes(c))
  }), [setColumns])

  const handleExport = useCallback(() => {
    setVisible(false)
    onExport({ columns, type: format })
  }, [columns, format, onExport])

  return (
    <>
      <Tooltip title={t('Export')}>
        <IconButton icon={faFileExport} onClick={() => setVisible(true)} />
      </Tooltip>
      <Modal
        className='sectors-export-modal'
        cancelText={cancelText}
        okText={okText}
        okButtonProps={{ disabled: columns.length === 0 }}
        title={
          <div className='sectors-export-title'>
            <span className='sectors-export-title-icon'><FontAwesomeIcon icon={faFileExport} /></span>
            <div className='flex-column'>
              <span className='sectors-export-title-text'>{t('sectors_export.title')}</span>
              <span className='sectors-export-title-subtitle'>{t('sectors_export.subtitle')}</span>
            </div>
          </div>
        }
        visible={visible}
        width={720}
        onCancel={() => setVisible(false)}
        onOk={handleExport}
      >
        <div className='sectors-export-toolbar'>
          <Badge
            className='sectors-export-counter'
            count={t('sectors_export.selected_count', { count: columns.length, total: ALL_COLUMNS.length })}
            style={{ backgroundColor: columns.length === 0 ? '#B22222' : '#3c8dbc' }}
          />
          <div className='flex-fill' />
          <IconButton
            icon={faCheckDouble}
            size='small'
            text={t('sectors_export.select_all')}
            disabled={columns.length === ALL_COLUMNS.length}
            onClick={() => setColumns(ALL_COLUMNS)}
          />
          <IconButton
            icon={faEraser}
            size='small'
            text={t('sectors_export.clear_all')}
            disabled={columns.length === 0}
            onClick={() => setColumns([])}
          />
        </div>
        <div className='sectors-export-sections'>
          {SECTIONS.map(section => {
            const count = section.columns.filter(c => selected.has(c)).length

            return (
              <section className='sectors-export-section' key={section.key}>
                <button
                  type='button'
                  className='sectors-export-section-header'
                  title={t('sectors_export.toggle_section')}
                  onClick={() => handleToggleSection(section)}
                >
                  <span className='sectors-export-section-icon'><FontAwesomeIcon icon={section.icon} /></span>
                  <span className='sectors-export-section-title'>{t(`sectors_export.sections.${section.key}`)}</span>
                  <span className={`sectors-export-section-count ${count === section.columns.length ? 'full' : ''}`}>
                    {count} / {section.columns.length}
                  </span>
                </button>
                <div className='sectors-export-section-body'>
                  {section.columns.map(column => (
                    <Checkbox
                      key={column}
                      checked={selected.has(column)}
                      onChange={() => handleToggleColumn(column)}
                    >
                      {t(`sectors_export.columns.${column}`)}
                    </Checkbox>
                  ))}
                </div>
              </section>
            )
          })}
        </div>
        <div className='sectors-export-format'>
          <span className='sectors-export-format-label'>{t('sectors_export.format')}</span>
          <Radio.Group buttonStyle='solid' value={format} onChange={e => setFormat(e.target.value)}>
            {FORMATS.map(f => (
              <Radio.Button key={f.value} value={f.value}>
                <FontAwesomeIcon icon={f.icon} /> &nbsp; {f.label}
              </Radio.Button>
            ))}
          </Radio.Group>
        </div>
      </Modal>
    </>
  )
}

export default SectorsExport
